import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFetchCommentRepliesMutation } from '@/hooks/comments/useFetchCommentRepliesMutation';
import { type Comment as CommentData, type GetNodeCommentsResponse } from '@/types/comments';
import { useCommentStore } from '@/hooks/data/comments/useCommentStore';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { DEFAULT_VISIBLE_REPLIES_COUNT } from '@/lib/comments';
import { deleteNodeComment } from '@/services/comment.service';
import { useShareConfig } from '@/context/ShareConfigContext';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useDeleteNodeCommentMutation = () => {
  const tToasts = useTranslations('toasts');
  const tCommon = useTranslations('common');
  const tErrors = useTranslations('errors');

  const { toast } = useToast();

  const setDeletingCommentId = useCommentStore((state) => state.setDeletingCommentId);
  const deleteNodeCommentWithAuth = useAuthenticatedQueryFn(deleteNodeComment);
  const queryClient = useQueryClient();
  const { fetchCommentRepliesMutation } = useFetchCommentRepliesMutation();
  const shareConfig = useShareConfig();

  const deleteNodeCommentMutation = useMutation({
    mutationFn: ({
      id,
    }: {
      id: string;
      nodeId: string;
      parentCommentId?: string;
      onSuccessCallback?: (deletedCommentId: string) => void;
    }) => deleteNodeCommentWithAuth({ id }),
    onMutate: ({ id }) => {
      setDeletingCommentId(id);
    },
    onSuccess: async (_response, { id, nodeId, parentCommentId, onSuccessCallback }) => {
      try {
        // Get all query keys that match the node comment list pattern
        const queryCache = queryClient.getQueryCache();

        const queries = queryCache.findAll({ queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id] });

        // Before touching the cache, determine which threads will need backfilling
        // after the reply is removed, and fetch the replacement replies upfront.
        const backfillMap = new Map<string, Array<CommentData>>();

        for (const query of queries) {
          const currentData = queryClient.getQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey);
          if (!currentData) {
            continue;
          }

          for (const page of currentData.pages) {
            for (const comment of page.comments) {
              // Only reply deletions can require backfilling
              const isAffected = parentCommentId
                ? comment.commentId === parentCommentId
                : comment.latestReplies.some((reply) => reply.id === id);

              if (!isAffected || backfillMap.has(comment.commentId)) {
                continue;
              }

              // Only backfill if the deleted reply is actually in latestReplies
              const isReplyInLatestReplies = comment.latestReplies.some((reply) => reply.id === id);
              if (!isReplyInLatestReplies) {
                continue;
              }

              const newRepliesCount = Math.max(comment.repliesCount - 1, 0);
              const newLatestRepliesCount = comment.latestReplies.length - 1;
              const minLatest = Math.min(DEFAULT_VISIBLE_REPLIES_COUNT, newRepliesCount);

              if (newLatestRepliesCount < minLatest) {
                const neededCount = minLatest - newLatestRepliesCount;
                const offset = newRepliesCount - minLatest;

                try {
                  const result = await fetchCommentRepliesMutation.mutateAsync({
                    commentId: comment.commentId,
                    limit: neededCount,
                    offset,
                    shareId: shareConfig?.id,
                  });
                  backfillMap.set(comment.commentId, result.replies);
                } catch {
                  // If the fetch fails, we'll still apply the deletion without backfill
                }
              }
            }
          }
        }

        // Apply the deletion (and any backfill) in a single cache update per query
        queries.forEach((query) => {
          queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
            if (!oldData) {
              return oldData;
            }

            // Check if the deleted comment is a root comment
            const isDeletedRootComment = oldData.pages.some((page) =>
              page.comments.some((comment) => comment.commentId === id),
            );

            if (isDeletedRootComment) {
              return {
                ...oldData,
                pages: oldData.pages.map((page) => {
                  const hasDeletedComment = page.comments.some((comment) => comment.commentId === id);

                  if (!hasDeletedComment) {
                    return page;
                  }

                  return {
                    ...page,
                    comments: page.comments.filter((comment) => comment.commentId !== id),
                    pagination: {
                      ...page.pagination,
                      count: page.pagination.count - 1,
                    },
                  };
                }),
              };
            }

            // Handle reply deletion — include any pre-fetched backfill replies in one pass
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                comments: page.comments.map((comment) => {
                  const shouldPatchComment = parentCommentId
                    ? comment.commentId === parentCommentId
                    : comment.latestReplies.some((reply) => reply.id === id);

                  if (!shouldPatchComment) {
                    return comment;
                  }

                  const filteredLatestReplies = comment.latestReplies.filter((reply) => reply.id !== id);
                  const backfillReplies = backfillMap.get(comment.commentId) ?? [];

                  return {
                    ...comment,
                    latestReplies: [...backfillReplies, ...filteredLatestReplies],
                    repliesCount: Math.max(comment.repliesCount - 1, 0),
                  };
                }),
              })),
            };
          });
        });

        toast({
          title: tToasts('deleteNodeCommentMutation.title'),
          description: tToasts('deleteNodeCommentMutation.description'),
        });

        onSuccessCallback?.(id);
      } catch (err) {
        errorLog(err);
      }
      setDeletingCommentId(null);
    },
    onError: async (err: any, { nodeId }) => {
      // Invalidate in case the user tries to delete a deleted comment.
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description:
          err.status === 404
            ? tToasts('deleteNodeCommentMutation.error')
            : (err.message ?? tErrors('toastDefaultDescription')),
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id],
      });
      setDeletingCommentId(null);
    },
  });

  return {
    deleteNodeCommentMutation,
  };
};
